import { supabase } from './supabase';

declare global {
  interface Window {
    /** Door de native iOS-schil gezet vóórdat de webapp laadt. */
    __APNS_DEVICE_TOKEN__?: string;
  }
}

const SW_VERSION = '2';

export function isLocalhostEnvironment(): boolean {
  return location.hostname === 'localhost' || location.hostname === '127.0.0.1';
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service workers not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register(
      `/service-worker.js?v=${SW_VERSION}`,
    );

    if (registration.active) return registration;

    const sw = registration.installing || registration.waiting;
    if (sw) {
      await new Promise<void>((resolve) => {
        sw.addEventListener('statechange', function handler() {
          if (sw.state === 'activated') {
            sw.removeEventListener('statechange', handler);
            resolve();
          }
        });
        if (sw.state === 'activated') resolve();
      });
    }

    return registration;
  } catch (err) {
    console.error('Service worker registration failed:', err);
    return null;
  }
}

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function requestPushPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) return 'denied';
  return Notification.requestPermission();
}

export async function subscribeToPush(
  _registration: ServiceWorkerRegistration,
  vapidPublicKey: string,
): Promise<PushSubscription> {
  const activeReg = await navigator.serviceWorker.ready;

  const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

  if (applicationServerKey.length !== 65) {
    throw new Error(
      `Ongeldige VAPID key lengte (${applicationServerKey.length} bytes, verwacht 65). Neem contact op met de beheerder.`,
    );
  }

  // Always unsubscribe first to handle VAPID key changes cleanly
  try {
    const existing = await activeReg.pushManager.getSubscription();
    if (existing) {
      await existing.unsubscribe();
    }
  } catch (unsubErr) {
    console.warn('Could not unsubscribe existing push subscription:', unsubErr);
  }

  try {
    const subscription = await activeReg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
    return subscription;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error('PushManager.subscribe failed:', msg);

    if (isLocalhostEnvironment()) {
      throw new Error(
        'Push notificaties werken niet op localhost. Deploy de app naar een HTTPS-domein om push te testen.',
      );
    }

    if (msg.includes('public key') || msg.includes('applicationServerKey')) {
      console.warn('VAPID key mismatch detected, attempting full SW re-register...');
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const reg of registrations) {
          const sub = await reg.pushManager.getSubscription().catch(() => null);
          if (sub) await sub.unsubscribe().catch(() => {});
          await reg.unregister();
        }
        const freshReg = await navigator.serviceWorker.register(
          `/service-worker.js?v=${SW_VERSION}`,
        );
        await navigator.serviceWorker.ready;
        const retrySubscription = await freshReg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });
        return retrySubscription;
      } catch (retryErr) {
        console.error(
          'Retry after SW re-register also failed:',
          retryErr instanceof Error ? retryErr.message : retryErr,
        );
        throw new Error(
          'Push notificaties konden niet worden ingeschakeld. Ga naar je browserinstellingen, verwijder de service worker voor deze site, en probeer het opnieuw.',
        );
      }
    }
    throw err;
  }
}

export async function savePushSubscription(subscription: PushSubscription): Promise<boolean> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return false;

  const subJson = subscription.toJSON();
  const deviceInfo = `${navigator.userAgent.substring(0, 100)}`;

  const { data: existingList } = await supabase
    .from('push_subscriptions')
    .select('id, subscription')
    .eq('user_id', user.id);

  const existing = (existingList || []).find(
    (row) => (row.subscription as Record<string, unknown> | null)?.endpoint === subJson.endpoint,
  );

  if (existing) {
    const { error } = await supabase
      .from('push_subscriptions')
      .update({ subscription: subJson, device_info: deviceInfo })
      .eq('id', existing.id);
    if (error) {
      console.error('Error updating push subscription:', error);
      return false;
    }
  } else {
    const { error } = await supabase.from('push_subscriptions').insert({
      user_id: user.id,
      subscription: subJson,
      device_info: deviceInfo,
    });
    if (error) {
      console.error('Error saving push subscription:', error);
      return false;
    }
  }
  return true;
}

export async function unsubscribeFromPush(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
    }

    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (user) {
      await supabase.from('push_subscriptions').delete().eq('user_id', user.id);
    }
    return true;
  } catch (err) {
    console.error('Error unsubscribing:', err);
    return false;
  }
}

export async function getCurrentPushSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  try {
    const registration = await navigator.serviceWorker.ready;
    return registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isNativeApp(): boolean {
  return navigator.userAgent.includes('PrachtNativeApp');
}

let apnsTokenSaved = false;

async function saveApnsToken(token: string): Promise<void> {
  if (apnsTokenSaved) return;
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return;

  const { data: existing } = await supabase
    .from('push_subscriptions')
    .select('id')
    .eq('user_id', user.id)
    .eq('platform', 'ios')
    .eq('device_token', token)
    .maybeSingle();

  if (existing) {
    apnsTokenSaved = true;
    return;
  }

  const { error } = await supabase.from('push_subscriptions').insert({
    user_id: user.id,
    platform: 'ios',
    device_token: token,
    subscription: {},
    device_info: navigator.userAgent.substring(0, 100),
  });

  if (!error) apnsTokenSaved = true;
}

export function initApnsTokenListener(): void {
  if (!isNativeApp()) return;

  const existingToken = window.__APNS_DEVICE_TOKEN__;
  if (existingToken) {
    saveApnsToken(existingToken);
  }

  window.addEventListener('apns-token', ((e: CustomEvent) => {
    if (e.detail) saveApnsToken(e.detail);
  }) as EventListener);
}
